/**
 * Period-over-period comparison table + CSV export for Cached Performance
 * (CR-4751). Rows: metric. Columns: Current | Previous | Delta | 3-point avg.
 */
import { Download } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table'
import { useFormatters } from '../../../contexts/PreferencesContext'
import {
  DAILY_HIDDEN_METRICS, type DashboardSnapshot, type MetricType, type PeriodType, type TrendPoint,
} from '../../../lib/api/dashboard-snapshots'
import type { DeliveryMetricMode } from '../../../lib/api'
import {
  DAILY_HIDDEN_NOTE, LOWER_IS_BETTER, METRIC_LABELS, NA_LABEL, NA_REASON, asNumber, deltaTone,
  formatValueByUnit, percentDelta, downloadCSV, formatDate,
} from './helpers'
import { showsOtd, showsOtif } from '../deliveryMetricMode'

interface CachedComparisonTableProps {
  latest: DashboardSnapshot[]
  trends: Record<MetricType, TrendPoint[]>
  period?: PeriodType
  /** Period metrics with nothing to measure in the latest capture (CR-4751). */
  naMetrics?: Set<MetricType>
  /** CR-8697: the customer's Delivery Metric Mode — drops the OTIF / OTD rows it switches off. */
  deliveryMetricMode?: DeliveryMetricMode
}

const COMPARISON_METRICS: MetricType[] = [
  'open_order_value', 'total_spend', 'monthly_spend',
  'arrears_value', 'arrears_count',
  'otif_percentage', 'otd_percentage',
  'ppm', 'ncr_count', 'ncr_open_count',
  'active_supplier_count', 'appeals_count',
]

/**
 * CR-4751: the date a row's figure is AS OF — the end of the period it
 * measures. September's monthly row is captured on 1 October; showing the
 * capture date read "As of 01/10" beside charts labelled September.
 */
function asOf(s: DashboardSnapshot): string {
  return s.window_end ?? s.snapshot_date
}

/** CR-4751: green is good news, not "went up" — arrears or PPM rising read green. */
function deltaColorClass(metric: MetricType, delta: number): string {
  const { good } = deltaTone(delta, LOWER_IS_BETTER.includes(metric))
  if (good === null) return 'text-muted-foreground'
  return good ? 'text-green-600' : 'text-red-600'
}

export function CachedComparisonTable({
  latest, trends, period = 'monthly', naMetrics = new Set<MetricType>(), deliveryMetricMode,
}: CachedComparisonTableProps) {
  const { formatCurrency, formatNumber, formatDate: ctxFormatDate } = useFormatters()
  // CR-4751: money in the currency the API says it is in.
  const fmt = (value: string | number, unit: DashboardSnapshot['metric_unit'], currency?: string | null) =>
    formatValueByUnit(value, unit, (n) => formatCurrency(n, currency ?? undefined), (n) => formatNumber(n))

  const latestByMetric = new Map<MetricType, DashboardSnapshot>()
  latest.forEach((s) => latestByMetric.set(s.metric_type, s))

  // CR-4751: Daily does not measure OTIF, OTD or active suppliers.
  // CR-8697: nor is a metric the Delivery Metric Mode switches off listed (or exported).
  const metrics = COMPARISON_METRICS.filter((m) => (
    !(period === 'daily' && DAILY_HIDDEN_METRICS.includes(m))
    && !(m === 'otif_percentage' && !showsOtif(deliveryMetricMode))
    && !(m === 'otd_percentage' && !showsOtd(deliveryMetricMode))
  ))

  const rows = metrics.map((metric) => {
    const snap = naMetrics.has(metric) ? undefined : latestByMetric.get(metric)
    const series = trends[metric] ?? []
    const previous = series.length >= 2 ? series[series.length - 2] : undefined
    const last3 = series.slice(-3)
    const avg3 = last3.length > 0
      ? last3.reduce((sum, p) => sum + asNumber(p.metric_value), 0) / last3.length
      : null
    const delta = snap && previous
      ? percentDelta(asNumber(snap.metric_value), asNumber(previous.metric_value))
      : null
    return { metric, snap, previous, avg3, delta }
  })

  const handleExport = () => {
    const header = [
      'Metric', 'Current Value', 'Current Date', 'Previous Value', 'Previous Date', '3-Point Avg', 'Delta %',
      'Currency',
    ]
    const csvRows: string[][] = [header]
    rows.forEach((r) => {
      if (!r.snap) return
      csvRows.push([
        METRIC_LABELS[r.metric],
        r.snap.metric_value,
        asOf(r.snap),
        r.previous?.metric_value ?? '',
        r.previous ? (r.previous.window_end ?? r.previous.snapshot_date) : '',
        r.avg3 !== null ? r.avg3.toFixed(2) : '',
        r.delta !== null ? r.delta.toFixed(2) : '',
        r.snap.currency ?? '',
      ])
    })
    downloadCSV(csvRows, `performance-history-${new Date().toISOString().slice(0, 10)}.csv`)
  }

  const hasRows = rows.some((r) => r.snap)

  return (
    <Card className="shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">Period Comparison</CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={!hasRows}
        >
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Metric</TableHead>
              <TableHead>Current</TableHead>
              <TableHead>Previous</TableHead>
              <TableHead>3-point avg</TableHead>
              <TableHead>Delta</TableHead>
              <TableHead>As of</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              if (!r.snap) {
                return (
                  <TableRow key={r.metric}>
                    <TableCell className="font-medium">{METRIC_LABELS[r.metric]}</TableCell>
                    <TableCell colSpan={5} className="text-muted-foreground text-sm">
                      {naMetrics.has(r.metric) ? `${NA_LABEL} — ${NA_REASON.toLowerCase()}` : 'No snapshot captured yet'}
                    </TableCell>
                  </TableRow>
                )
              }
              const unit = r.snap.metric_unit
              const ccy = r.snap.currency
              return (
                <TableRow key={r.metric}>
                  <TableCell className="font-medium">{METRIC_LABELS[r.metric]}</TableCell>
                  <TableCell>{fmt(r.snap.metric_value, unit, ccy)}</TableCell>
                  <TableCell>
                    {r.previous ? fmt(r.previous.metric_value, unit, r.previous.currency ?? ccy) : '—'}
                  </TableCell>
                  <TableCell>
                    {r.avg3 !== null ? fmt(r.avg3, unit, ccy) : '—'}
                  </TableCell>
                  <TableCell>
                    {r.delta === null ? '—' : (
                      <span className={deltaColorClass(r.metric, r.delta)}>
                        {r.delta > 0 ? '+' : ''}{r.delta.toFixed(1)}%
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(asOf(r.snap), ctxFormatDate)}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        {period === 'daily' && (
          <p className="text-xs text-muted-foreground mt-2">{DAILY_HIDDEN_NOTE}</p>
        )}
      </CardContent>
    </Card>
  )
}
